import { Component, ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Button from "@/lib/components/ui/Button";

import styles from "./error-boundary.module.scss";

interface ErrorBoundaryState {
  hasError: boolean;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  locationKey?: string;
}

class ErrorBoundaryInner extends Component<
  ErrorBoundaryProps & { fallback: ReactNode },
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps & { fallback: ReactNode }) {
    if (
      this.state.hasError &&
      prevProps.locationKey !== this.props.locationKey
    ) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

function ErrorFallback(): JSX.Element {
  const { t } = useTranslation("translation");
  const tx = (key: string) => t(key as any);
  const navigate = useNavigate();

  return (
    <div className={styles.container}>
      <span className={styles.errorIcon}>⚠</span>
      <h1 className={styles.title}>{tx("errorBoundaryTitle")}</h1>
      <p className={styles.description}>{tx("errorBoundaryDescription")}</p>
      <div className={styles.actions}>
        <Button
          variant="primary"
          size="l"
          iconName="home"
          onClick={() => navigate("/")}
        >
          {tx("goToHome")}
        </Button>
        <Button
          variant="secondary"
          size="l"
          iconName="sync"
          onClick={() => window.location.reload()}
        >
          {tx("refresh")}
        </Button>
      </div>
    </div>
  );
}

export function ErrorBoundary({
  children,
}: Readonly<{ children: ReactNode }>): JSX.Element {
  const location = useLocation();

  return (
    <ErrorBoundaryInner fallback={<ErrorFallback />} locationKey={location.key}>
      {children}
    </ErrorBoundaryInner>
  );
}
